import type { DiscrepancyCardViewModel } from "@/components/reconciliation/result/lib/view-model";
import type { Virtualizer } from "@tanstack/react-virtual";
/**
 * useReportFilters — фильтрация и навигация в отчёте.
 *
 * Управляет локальным state страницы отчёта:
 * - searchQuery + searchQueryDebounced (300ms);
 * - highlightedDiscrepancyId — ID карточки для подсветки drill-down target,
 *   автоматически сбрасывается через 2 сек;
 * - filteredCards — derived массив карточек, удовлетворяющих поиску;
 * - actions: setSearchQuery, resetFilters, highlightDiscrepancy, scrollToDiscrepancy.
 *
 * Поиск (case-insensitive substring) ищет в `groupKey`, `summary` и в
 * `workName` любой строки `workOrderSources`/`actSources`.
 *
 * Search query trim'ится и обрезается до 200 символов.
 *
 * Фича 026: severity и level фильтры убраны вместе с chip-UI. После
 * унификации pipeline level не имеет смысла фильтровать (один отчёт =
 * оба уровня); severity редко нужен с master-detail UI.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const SEARCH_DEBOUNCE_MS = 300;
const HIGHLIGHT_DURATION_MS = 2000;
const SEARCH_MAX_LEN = 200;

export interface UseReportFiltersOptions {
	/** Полный список карточек до фильтрации. */
	cards: DiscrepancyCardViewModel[];
}

export interface UseReportFiltersResult {
	searchQuery: string;
	searchQueryDebounced: string;
	highlightedDiscrepancyId: string | null;
	filteredCards: DiscrepancyCardViewModel[];
	isFiltering: boolean;
	setSearchQuery: (query: string) => void;
	resetFilters: () => void;
	highlightDiscrepancy: (id: string | null) => void;
	scrollToDiscrepancy: (
		id: string,
		virtualizerRef: Virtualizer<HTMLDivElement, Element> | null,
	) => boolean;
}

/**
 * Pure-функция фильтрации (extracted для тестируемости).
 *
 * @returns подмассив `cards`, удовлетворяющий поиску.
 */
export function applyFilters(
	cards: DiscrepancyCardViewModel[],
	searchQueryDebounced: string,
): DiscrepancyCardViewModel[] {
	const search = searchQueryDebounced.trim().toLowerCase();
	if (search.length === 0) return cards;
	return cards.filter((card) => cardMatchesSearch(card, search));
}

function cardMatchesSearch(card: DiscrepancyCardViewModel, search: string): boolean {
	if (card.groupKey.toLowerCase().includes(search)) return true;
	if (card.summary.toLowerCase().includes(search)) return true;
	for (const source of card.workOrderSources) {
		if (source.workName.toLowerCase().includes(search)) return true;
		if (source.rateNumber?.toLowerCase().includes(search)) return true;
	}
	for (const source of card.actSources) {
		if (source.workName.toLowerCase().includes(search)) return true;
		if (source.code?.toLowerCase().includes(search)) return true;
	}
	return false;
}

export function useReportFilters({ cards }: UseReportFiltersOptions): UseReportFiltersResult {
	const [searchQuery, setSearchQueryRaw] = useState("");
	const [searchQueryDebounced, setSearchQueryDebounced] = useState("");
	const [highlightedDiscrepancyId, setHighlightedDiscrepancyId] = useState<string | null>(null);
	const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		const id = setTimeout(() => {
			setSearchQueryDebounced(searchQuery);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(id);
	}, [searchQuery]);

	useEffect(() => {
		return () => {
			if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
		};
	}, []);

	const setSearchQuery = useCallback((query: string) => {
		const trimmed = query.slice(0, SEARCH_MAX_LEN);
		setSearchQueryRaw(trimmed);
	}, []);

	const resetFilters = useCallback(() => {
		setSearchQueryRaw("");
		setSearchQueryDebounced("");
		if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
		setHighlightedDiscrepancyId(null);
	}, []);

	const highlightDiscrepancy = useCallback((id: string | null) => {
		if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
		setHighlightedDiscrepancyId(id);
		if (id !== null) {
			highlightTimerRef.current = setTimeout(() => {
				setHighlightedDiscrepancyId(null);
				highlightTimerRef.current = null;
			}, HIGHLIGHT_DURATION_MS);
		}
	}, []);

	const filteredCards = useMemo(
		() => applyFilters(cards, searchQueryDebounced),
		[cards, searchQueryDebounced],
	);

	const isFiltering = searchQueryDebounced.trim().length > 0;

	const scrollToDiscrepancy = useCallback(
		(id: string, virtualizerRef: Virtualizer<HTMLDivElement, Element> | null): boolean => {
			const index = cards.findIndex((c) => c.id === id);
			if (index === -1) return false;
			if (virtualizerRef) {
				virtualizerRef.scrollToIndex(index, { align: "center", behavior: "smooth" });
			} else {
				const el = document.querySelector<HTMLElement>(`[data-discrepancy-id="${id}"]`);
				el?.scrollIntoView({ behavior: "smooth", block: "center" });
			}
			return true;
		},
		[cards],
	);

	return {
		searchQuery,
		searchQueryDebounced,
		highlightedDiscrepancyId,
		filteredCards,
		isFiltering,
		setSearchQuery,
		resetFilters,
		highlightDiscrepancy,
		scrollToDiscrepancy,
	};
}
